// UpdateUser.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { fetchData, updateUserApi } from "../../services/apiServices";

const UpdateUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [profileFile, setProfileFile] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [isActive, setIsActive] = useState(true);

  // Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const response = await fetchData(`api/v2/admin/auth/users/${id}`);
        if (response.status && response.data) {
          const u = response.data;
          setUser(u);
          setFirstName(u.firstName || "");
          setLastName(u.lastName || "");
          setEmail(u.email || "");
          setPhoneNumber(u.phone_number || "");
          setPreviewImage(u.profileImage || "");
          setIsActive(u.isActive ?? true);
        } else {
          setError("Failed to fetch user details");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch user details");
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  // Handle profile file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid image type");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size exceeds 5MB");
      return;
    }

    setProfileFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (e) => {
    setProfileImageUrl(e.target.value);
    setPreviewImage(e.target.value);
    setProfileFile(null);
  };

  const handleStatusToggle = () => setIsActive(!isActive);

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstName || !lastName || !email) {
      toast.error("First name, last name, and email are required");
      return;
    }

    try {
      setUpdating(true);

      // Prepare data object with only changed fields
      const updatedFields = {};
      if (firstName !== user.firstName) updatedFields.firstName = firstName;
      if (lastName !== user.lastName) updatedFields.lastName = lastName;
      if (email !== user.email) updatedFields.email = email;
      if (phoneNumber !== user.phone_number)
        updatedFields.phone_number = phoneNumber;
      if (isActive !== (user.isActive ?? true))
        updatedFields.isActive = isActive;
      if (profileImageUrl) updatedFields.profileImageUrl = profileImageUrl;
      if (profileFile) updatedFields.file = profileFile; // assume updateUserApi can handle FormData

      if (Object.keys(updatedFields).length === 0) {
        toast.info("No changes detected");
        setUpdating(false);
        return;
      }

      const response = await updateUserApi(id, updatedFields);

      if (response.status) {
        toast.success("User updated successfully");
        navigate(`/users/${id}`);
      } else {
        toast.error(response.message || "Failed to update user");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating user");
    } finally {
      setUpdating(false);
    }
  };

  const handleReset = () => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.email || "");
      setPhoneNumber(user.phone_number || "");
      setProfileImageUrl("");
      setProfileFile(null);
      setPreviewImage(user.profileImage || "");
      setIsActive(user.isActive ?? true);
    }
  };

  if (loading) {
    return (
      <Container className="text-center p-5">
        <Spinner color="primary" />
        <p className="mt-2">Loading user details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert color="danger">{error}</Alert>
        <Button color="secondary" onClick={() => navigate("/users")}>
          Back to Users
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="page-content">
      <Breadcrumbs title="Admin" breadcrumbItem="Update User" />

      <Row className="mb-3">
        <Col>
          <h4 className="mb-0">
            Update User: {user.firstName} {user.lastName}
          </h4>
          <p className="text-muted">ID: {user._id}</p>
        </Col>
        <Col className="text-end">
          <Button
            color="secondary"
            onClick={() => navigate(`/users/${id}`)}
            className="me-2">
            <i className="ri-arrow-left-line"></i> Back to Details
          </Button>
          <Button color="info" onClick={() => navigate("/users")}>
            <i className="ri-list-check"></i> View All Users
          </Button>
        </Col>
      </Row>

      <Row>
        <Col lg={8}>
          <Card className="shadow-sm border-0">
            <CardHeader className="bg-primary text-white">
              <h5 className="mb-0">Edit User Details</h5>
            </CardHeader>
            <CardBody>
              <Form onSubmit={handleSubmit}>
                {/* First & Last Name */}
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="firstName" className="fw-bold">
                        First Name *
                      </Label>
                      <Input
                        type="text"
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Enter first name"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="lastName" className="fw-bold">
                        Last Name *
                      </Label>
                      <Input
                        type="text"
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Enter last name"
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Email & Phone */}
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="email" className="fw-bold">
                        Email *
                      </Label>
                      <Input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter email"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="phoneNumber" className="fw-bold">
                        Phone Number
                      </Label>
                      <Input
                        type="text"
                        id="phoneNumber"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="Enter phone number"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Profile Image URL & File */}
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="imageUrl" className="fw-bold">
                        Profile Image URL
                      </Label>
                      <Input
                        type="url"
                        id="imageUrl"
                        value={profileImageUrl}
                        onChange={handleImageUrlChange}
                        placeholder="Enter image URL"
                      />
                      <small className="text-muted">
                        Leave empty if uploading file
                      </small>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="fileUpload" className="fw-bold">
                        Upload Profile Image
                      </Label>
                      <Input
                        type="file"
                        id="fileUpload"
                        onChange={handleFileChange}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                      />
                      <small className="text-muted">
                        Max 5MB. JPEG, PNG, WEBP, GIF
                      </small>
                    </FormGroup>
                  </Col>
                </Row>

                {previewImage && (
                  <FormGroup>
                    <Label className="fw-bold d-block mb-2">
                      Image Preview
                    </Label>
                    <div className="border rounded p-3 text-center bg-light">
                      <img
                        src={previewImage}
                        alt="Preview"
                        className="img-fluid rounded mb-2"
                        style={{ maxHeight: "200px", objectFit: "contain" }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "https://via.placeholder.com/200x200?text=Image+Not+Found";
                        }}
                      />
                    </div>
                  </FormGroup>
                )}

                {/* Status */}
                <FormGroup className="mt-3">
                  <Label className="fw-bold">Status</Label>
                  <div>
                    <Button
                      color={isActive ? "success" : "secondary"}
                      onClick={handleStatusToggle}
                      style={{ minWidth: "60px" }}>
                      {isActive ? "Active" : "Inactive"}
                    </Button>
                  </div>
                </FormGroup>

                {/* Action Buttons */}
                <div className="mt-4 pt-3 border-top">
                  <Button
                    type="submit"
                    color="primary"
                    className="me-2"
                    disabled={updating}>
                    {updating ? (
                      <>
                        <Spinner size="sm" className="me-2" /> Updating...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-2"></i> Update User
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    onClick={handleReset}
                    className="me-2">
                    <i className="ri-refresh-line me-2"></i> Reset
                  </Button>
                  <Button
                    type="button"
                    color="light"
                    onClick={() => navigate(`/users/${id}`)}>
                    Cancel
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>

        {/* User Info */}
        <Col lg={4}>
          <Card className="shadow-sm border-0">
            <CardHeader className="bg-info text-white">
              <h5 className="mb-0">
                <i className="ri-information-line me-2"></i> User Info
              </h5>
            </CardHeader>
            <CardBody>
              <div className="d-flex flex-column gap-2">
                <div>
                  <strong>ID:</strong> {user._id}
                </div>
                <div>
                  <strong>Email:</strong> {user.email}
                </div>
                <div>
                  <strong>Created At:</strong>{" "}
                  {new Date(user.createdAt).toLocaleString()}
                </div>
                <div>
                  <strong>Last Updated:</strong>{" "}
                  {new Date(user.updatedAt).toLocaleString()}
                </div>
                <div>
                  <strong>Level:</strong> {user.level}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UpdateUser;
